import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { SearchInput } from '@ui';
import { Check, X } from '@ui/icons';
import { useExpenseTypes } from '@entities/expense-type';
import { t } from '@shared/i18n';

/** A selected category is identified by its backend `_id` (sent as `expense_type_id`). */
export type CategoryId = string;

export interface CategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value?: CategoryId | undefined;
  onSelect: (id: CategoryId) => void;
}

/** Body — remounts each open so the search query resets. Categories come from the API. */
function CategoryBody({
  value,
  onSelect,
}: {
  value: CategoryId | undefined;
  onSelect: (id: CategoryId) => void;
}) {
  const c = t.newBill.categoryModal;
  const { data: categories = [], isLoading } = useExpenseTypes();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const list = categories.filter((item) => item.name.toLowerCase().includes(q));
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden py-3">
      <div className="px-3">
        <SearchInput
          aria-label={c.search}
          placeholder={c.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
          className="rounded-lg"
        />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto px-3">
        {list.length === 0 ? (
          <li className="py-6 text-center text-sm text-muted">{isLoading ? c.loading : c.empty}</li>
        ) : (
          list.map((item) => (
            <li key={item.id} className="border-b border-divider last:border-b-0">
              <button
                type="button"
                onClick={() => onSelect(item.id)}
                className="flex w-full items-center gap-3  py-3 px-2 text-left hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface"
                  style={item.color ? { backgroundColor: item.color } : undefined}
                >
                  {item.image ? (
                    <img src={item.image} alt="" className="size-10.75 object-contain" />
                  ) : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-medium text-fg">{item.name}</span>
                  <span className="block text-sm italic text-muted">{item.description}</span>
                </span>
                {value === item.id ? (
                  <Check className="size-5 shrink-0 text-positive" aria-hidden />
                ) : null}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

/**
 * Expense category picker (Figma) — searchable list of categories with icon + description.
 * Opened from the New Bill description field; selecting one closes the modal.
 */
export function CategoryModal({ open, onOpenChange, value, onSelect }: CategoryModalProps) {
  const c = t.newBill.categoryModal;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-60 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-60 flex max-h-[80vh] w-[calc(100vw-2rem)] max-w-[560px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          <div className="flex items-center gap-4 border-b border-border bg-header-bar px-5 py-2">
            <Dialog.Title className="flex-1 text-lg font-semibold text-fg-strong">
              {c.title}
            </Dialog.Title>
            <Dialog.Close
              aria-label={t.newBill.close}
              className="flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft text-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          <CategoryBody
            value={value}
            onSelect={(id) => {
              onSelect(id);
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
